import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  ArrowRight, Activity, Globe, Zap } from 'lucide-react';

const RiceUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Rice University";

    return (
        <div className="min-h-screen bg-[#f7f7f7] font-sans text-[#00205B] selection:bg-[#7C7E7F] selection:text-white">
            {/* Top Bar */}
            <div className="bg-[#00205B] text-white py-3 px-6 md:px-12 flex justify-between items-center sticky top-0 z-50">
                <div className="flex items-center gap-2">
                    <span className="font-serif text-2xl font-bold tracking-wide">{schoolName.split(' ')[0].toUpperCase()}</span>
                </div>

                <div className="hidden lg:flex gap-8 font-bold text-xs uppercase tracking-widest text-gray-300">
                    <a href="#" className="hover:text-white transition-colors">About</a>
                    <a href="#" className="hover:text-white transition-colors">Academics</a>
                    <a href="#" className="hover:text-white transition-colors">Admission</a>
                    <a href="#" className="hover:text-white transition-colors">Research</a>
                    <a href="#" className="hover:text-white transition-colors">Campus</a>
                    <Search size={18} className="cursor-pointer hover:text-white" />
                </div>
                 <div className="lg:hidden text-white">
                    <Menu size={24} />
                </div>
            </div>

            {/* Hero - Unconventional Wisdom */}
            <header className="relative h-[600px] bg-[#00205B]">
                 <div className="absolute inset-0 opacity-40 mix-blend-overlay">
                    <img 
                        src="https://images.unsplash.com/photo-1565034946487-077786996e27?q=80&w=2070&auto=format&fit=crop" 
                        alt="Rice Campus" 
                        className="w-full h-full object-cover"
                    />
                 </div>
                 
                 <div className="relative z-10 max-w-[1200px] mx-auto h-full flex items-center px-6 md:px-12">
                     <div className="bg-white/10 backdrop-blur-md p-10 border-l-4 border-[#7C7E7F] max-w-xl">
                         <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                             Unconventional Wisdom.
                         </h1>
                         <p className="text-xl text-gray-200 mb-8 font-light">
                             A comprehensive research university that fosters diversity and an intellectual environment.
                         </p>
                         <Button className="bg-[#00205B] hover:bg-[#003080] text-white font-bold px-8 h-12 rounded-sm border border-white/30">
                             Apply to {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                 </div>
            </header>

            {/* Stats / Distinction */}
            <section className="py-20 px-6 md:px-12 bg-white">
                <div className="max-w-[1200px] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                         <div className="p-6">
                            <h3 className="text-[#00205B] font-bold text-xl uppercase tracking-widest mb-4">Ranked #1</h3>
                            <p className="text-gray-600">For "Happiest Students" and "Best Quality of Life" by Princeton Review.</p>
                         </div>
                         <div className="p-6 border-lborder-r md:border-x border-gray-100">
                            <h3 className="text-[#00205B] font-bold text-xl uppercase tracking-widest mb-4">6:1 Ratio</h3>
                            <p className="text-gray-600">Student-to-faculty ratio, ensuring close collaboration and mentorship.</p>
                         </div>
                         <div className="p-6">
                            <h3 className="text-[#00205B] font-bold text-xl uppercase tracking-widest mb-4">In Houston</h3>
                            <p className="text-gray-600">Located in the heart of the nation's fourth-largest city and energy capital.</p>
                         </div>
                    </div>
                </div>
            </section>

            {/* Focus Areas */}
            <section className="bg-[#f0f0f0] py-20 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-3xl font-serif text-[#00205B] mb-12 text-center">Research that Matters</h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { icon: Globe, title: "Global Health", desc: "Solving health challenges across borders." },
                            { icon: Zap, title: "Future Energy", desc: "Sustainable solutions for a changing world." },
                            { icon: Activity, title: "Data Science", desc: "Harnessing information for innovation." }
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-8 shadow-sm hover:shadow-lg transition-all rounded-sm group cursor-pointer border-t-4 border-transparent hover:border-[#00205B]">
                                <item.icon className="text-[#7C7E7F] mb-6 group-hover:text-[#00205B] transition-colors" size={40} />
                                <h3 className="text-2xl font-bold text-[#00205B] mb-4">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">{item.desc}</p>
                                <div className="flex items-center gap-2 text-sm font-bold text-[#00205B] uppercase tracking-widest">
                                    Explore <ArrowRight size={14} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#1a1a1a] text-gray-400 py-16 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 text-sm">
                    <div className="col-span-1 md:col-span-2">
                        <span className="text-white font-serif text-2xl font-bold tracking-wide block mb-6">{schoolName.split(' ')[0].toUpperCase()}</span>
                        <p className="mb-4 text-white">
                            6100 Main St, Houston, TX 77005-1827 <br/>
                            Mailing Address: P.O. Box 1892, Houston, TX 77251-1892 <br/>
                            (713) 348-0000
                        </p>
                        <div className="flex gap-4 mt-6">
                            <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-white transition-colors"></div>
                            <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-white transition-colors"></div>
                            <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-white transition-colors"></div>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-white font-bold uppercase tracking-widest mb-6 text-xs">Information For</h4>
                        <ul className="space-y-3">
                            <li><a href="#" className="hover:text-white">Prospective Students</a></li>
                            <li><a href="#" className="hover:text-white">Current Students</a></li>
                            <li><a href="#" className="hover:text-white">Faculty & Staff</a></li>
                            <li><a href="#" className="hover:text-white">Alumni</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold uppercase tracking-widest mb-6 text-xs">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="#" className="hover:text-white">Campus Map</a></li>
                            <li><a href="#" className="hover:text-white">Academic Calendar</a></li>
                            <li><a href="#" className="hover:text-white">Library</a></li>
                            <li><a href="#" className="hover:text-white">News & Media</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1200px] mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-xs">
                    © 2026 {schoolName} · Privacy Policy · Web Accessibility
                </div>
            </footer>
        </div>
    );
};

export default RiceUniversity;
